/*eslint max-lines: off */
import {
  FunctionComponent,
  lazy,
  Suspense,
  useState,
  useEffect,
  useMemo,
} from 'react'
import { Routes, Route, BrowserRouter, Navigate } from 'react-router-dom'
import {
  MinimalLoader,
  UrlParamTypes,
  usePlatformConfig,
  useWidgetContext,
} from '@dxp-module/common'
import { tokenService } from '@dxp-module/login'
import ROUTES from './common/enum/Routes'
import LoginOrSignup from './LoginOrSignup'
import ExpiredSessionModal from './common/components/sessionErrorModal/SessionErrorModal'
import OrdersLazyContainer from './myOrders/components/orders/OrdersLazyContainer'
import ReservationsLazyContainer from './myOrders/components/reservations/ReservationsLazyContainer'
import SavedCarsLazyContainer from './myOrders/components/savedCars/SavedCarsLazyContainer'
import EnquiryLazyContainer from './myOrders/components/enquiry/EnquiryLazyContainer'
import PersonalDetailsLazyContainer from './accountDetails/components/personalDetails/PersonalDetailsLazyContainer'
import ChangePasswordLazyContainer from './accountDetails/components/changePassword/ChangePasswordLazyContainer'
import FinanceListLazyContainer from './myFinance/components/FinanceListLazyContainer'
import InsuranceLazyContainer from './myInsurance/components/InsuranceLazyContainer'
import useRedirectRoute from './common/hooks/useRedirectRoute'
import SignUpLazyContainer from './signup/SignUpLazyContainer'
import LoginContainer from './LoginOrSignup/components/login/LoginContainer'
import LogoutLazyContainer from './accountDetails/components/personalDetails/LogoutLazyContainer'
import FinanceLazyContainer from './myFinance/components/FinanceLazyContainer'
import ServiceBookingLazyContainer from './myServiceBooking/components/ServiceBookingLazyContainer'
import collectedFilters from './common/services/collectedFilters'
import useUrlSearchParams from './common/hooks/useUrlSearchParams'

const MyAccount = lazy(() => import('./myAccount/MyAccount'))
const ForgotPassword = lazy(
  () => import('./common/components/forgotPassword/ForgotPassword')
)
const Login = lazy(() => import('./LoginOrSignup/components/login/Login'))
const SetPasswordContainer = lazy(
  () => import('./LoginOrSignup/components/setPassword/SetPasswordContainer')
)

const WidgetRouter: FunctionComponent = () => {
  const hasActiveSession = !!tokenService.getCurrentToken()
  const [isFirstLoad, setIsFirstLoad] = useState<boolean>(true)
  const { configuration } = useWidgetContext()
  const {
    platformConfig: {
      myAccount: { isSeparateSignupLogin },
    },
  } = usePlatformConfig()

  const {
    filters: { dxpPreview },
  } = collectedFilters()
  const { redirectRoute } = useRedirectRoute()
  const { token } = useUrlSearchParams() || {}

  useEffect(() => {
    setIsFirstLoad(false)
  }, [])

  const shouldRedirect = hasActiveSession && isFirstLoad

  const previewSearchParam = useMemo(
    () => (dxpPreview ? `${UrlParamTypes.DXP_PREVIEW}=${dxpPreview}` : ''),
    [dxpPreview]
  )

  const getRoute = () =>
    `${redirectRoute}${
      dxpPreview ? (token ? '&' : '?') + `${previewSearchParam}` : ''
    }`

  return (
    <BrowserRouter basename={configuration.basePath}>
      <ExpiredSessionModal />
      <Routes data-testid="myAccountRoot">
        <Route
          path={ROUTES.INDEX}
          element={
            shouldRedirect ? (
              <Navigate to={getRoute()} />
            ) : isSeparateSignupLogin ? (
              <LoginContainer />
            ) : (
              <LoginOrSignup />
            )
          }
        />
        <Route
          path={ROUTES.MYACCOUNT_BASE}
          element={
            <Suspense fallback={<MinimalLoader />}>
              <MyAccount />
            </Suspense>
          }
        >
          <Route
            path={ROUTES.MYACCOUNT_BASE}
            element={<Navigate to={getRoute()} />}
          />
          <Route
            path={ROUTES.MYACCOUNT_ORDERS}
            element={
              <Suspense fallback={<MinimalLoader />}>
                <OrdersLazyContainer />
              </Suspense>
            }
          />
          <Route
            path={ROUTES.MYACCOUNT_RESERVATIONS}
            element={
              <Suspense fallback={<MinimalLoader />}>
                <ReservationsLazyContainer />
              </Suspense>
            }
          />
          <Route
            path={ROUTES.MYACCOUNT_SAVED_CARS}
            element={
              <Suspense fallback={<MinimalLoader />}>
                <SavedCarsLazyContainer />
              </Suspense>
            }
          />
          <Route
            path={ROUTES.MYACCOUNT_ENQUIRY}
            element={
              <Suspense fallback={<MinimalLoader />}>
                <EnquiryLazyContainer />
              </Suspense>
            }
          />
          <Route
            path={ROUTES.MYACCOUNT_PERSONAL_DETAILS}
            element={
              <Suspense fallback={<MinimalLoader />}>
                <PersonalDetailsLazyContainer />
              </Suspense>
            }
          />
          <Route
            path={ROUTES.MYACCOUNT_LOGOUT}
            element={
              <Suspense fallback={<MinimalLoader />}>
                <LogoutLazyContainer />
              </Suspense>
            }
          />
          <Route
            path={ROUTES.MYACCOUNT_CHANGE_PASSWORD}
            element={
              <Suspense fallback={<MinimalLoader />}>
                <ChangePasswordLazyContainer />
              </Suspense>
            }
          />
          <Route
            path={ROUTES.MYACCOUNT_FINANCE_REDIRECTION}
            element={
              <Suspense fallback={<MinimalLoader />}>
                <FinanceListLazyContainer />
              </Suspense>
            }
          />
          <Route
            path={ROUTES.MYACCOUNT_FINANCE}
            element={
              <Suspense fallback={<MinimalLoader />}>
                <FinanceLazyContainer />
              </Suspense>
            }
          />
          <Route
            path={ROUTES.MYACCOUNT_INSURANCE}
            element={
              <Suspense fallback={<MinimalLoader />}>
                <InsuranceLazyContainer />
              </Suspense>
            }
          />
          <Route
            path={ROUTES.MYACCOUNT_SERVICEBOOKING_REDIRECTION}
            element={
              <Suspense fallback={<MinimalLoader />}>
                <ServiceBookingLazyContainer />
              </Suspense>
            }
          />
        </Route>
        <Route
          path={ROUTES.MYACCOUNT_FORGOT_PASSWORD}
          element={
            <Suspense fallback={<MinimalLoader />}>
              <ForgotPassword />
            </Suspense>
          }
        />
        <Route
          path={ROUTES.MYACCOUNT_LOGIN}
          element={
            shouldRedirect ? (
              <Navigate to={getRoute()} />
            ) : (
              <Suspense fallback={<MinimalLoader />}>
                <Login hideSignup />
              </Suspense>
            )
          }
        />
        <Route
          path={ROUTES.MYACCOUNT_SET_PASSWORD}
          element={
            <Suspense fallback={<MinimalLoader />}>
              <SetPasswordContainer />
            </Suspense>
          }
        />
        <Route
          path={ROUTES.MYACCOUNT_SIGNUP}
          element={
            <Suspense fallback={<MinimalLoader />}>
              <SignUpLazyContainer />
            </Suspense>
          }
        />
        <Route
          path="*"
          element={
            <Navigate
              to={
                hasActiveSession
                  ? `${ROUTES.MYACCOUNT_BASE}${
                      dxpPreview ? `?${previewSearchParam}` : ''
                    }`
                  : `${ROUTES.INDEX}${
                      dxpPreview ? `?${previewSearchParam}` : ''
                    }`
              }
            />
          }
        />
      </Routes>
    </BrowserRouter>
  )
}

export default WidgetRouter
